import { t } from '@lingui/macro';
import { createColumnHelper, Row } from '@tanstack/react-table';
import { getDurations } from 'applicationTable/applicationUtils';
import { TApplication } from 'lendingApi/applications.types';
import { toLower } from 'lodash';
import { ApplicantCell } from './Cells/ApplicantCell/ApplicantCell';
import { ApplicationStatusCell } from './Cells/ApplicationStatusCell/ApplicationStatusCell';
import { ApprovalAmountCell } from './Cells/ApprovalAmountCell/ApprovalAmountCell';
import { CompanyCell } from './Cells/CompanyCell/CompanyCell';
import { CreatedCell } from './Cells/CreatedCell/CreatedCell';
import { ExpiryDateCell } from './Cells/ExpiryDateCell/ExpiryDateCell';
import { RequestedAmountCell } from './Cells/RequestedAmountCell/RequestedAmountCell';
import { TermLengthCell } from './Cells/TermLengthCell/TermLengthCell';

export const COLUMN_IDS = {
  created: 'created',
  companyName: 'companyName',
  applicant: 'applicant',
  status: 'status',
  termLength: 'termLength',
  approvalAmount: 'approvalAmount',
  requestedAmount: 'requestedAmount',
  expiryDate: 'expiryDate',
  referredBy: 'referredBy',
} as const;

export const getDefaultColumns = (showReferredByColumn = false) => {
  const columnHelper = createColumnHelper<TApplication>();

  const filterByName = (
    row: Row<TApplication>,
    columnId: string,
    filterValue?: string,
  ) => {
    if (!filterValue) return true;

    const customer: TApplication['customer'] | TApplication['introducer'] =
      row.getValue(columnId);
    const fullName = `${customer?.first_name ?? ''} ${customer?.last_name ?? ''}`;

    return toLower(fullName).includes(toLower(filterValue));
  };

  const filterByDateRange = (
    row: Row<TApplication>,
    columnId: string,
    filterValue?: [string, string],
  ) => {
    if (!filterValue || (!filterValue[0] && !filterValue[1])) return true;
    const value: string = row.getValue(columnId);

    if (!value) return false;

    const dateValue = Date.parse(value);

    // falling back to infinity so open ended date ranges still work correctly
    const from = filterValue[0] ? Date.parse(filterValue[0]) : -Infinity;
    const to = filterValue[1] ? Date.parse(filterValue[1]) : Infinity;

    return dateValue >= from && dateValue <= to;
  };

  const filterByStatus = (
    row: Row<TApplication>,
    columnId: string,
    filterValues?: string[],
  ) => {
    if (!filterValues || filterValues.length === 0) return true;
    const rowVal = row.getValue<string>(columnId);
    return filterValues.includes(rowVal);
  };

  const filterByTermLength = (
    row: Row<TApplication>,
    columnId: string,
    filterValues?: string[],
  ) => {
    if (!filterValues?.length) return true;

    const rowValue = row.getValue<string>(columnId);
    const termLengthValues = rowValue
      .split(',')
      .map((termLength) => termLength.trim());
    return filterValues.some((filterValue) =>
      termLengthValues.includes(filterValue),
    );
  };

  const filterByNumberRange = (
    row: Row<TApplication>,
    columnId: string,
    filterValue?: [string?, string?],
  ) => {
    if (!filterValue || (!filterValue[0] && !filterValue[1])) return true;

    const rowValue = row.getValue(columnId);
    if (rowValue === null || rowValue === '') return false;

    const value = Number(rowValue);
    if (isNaN(value)) return false;

    const min = filterValue[0] ? Number(filterValue[0]) : -Infinity;
    const max = filterValue[1] ? Number(filterValue[1]) : Infinity;

    return value >= min && value <= max;
  };

  const columns = [
    columnHelper.accessor('created', {
      header: t`Created`,
      cell: (data) => (
        <CreatedCell
          date={data.getValue()}
          isPartnerChannel={data.row.original.is_partner_channel}
        />
      ),
      id: COLUMN_IDS.created,
      enableHiding: false,
      meta: {
        filterVariant: 'dateRange',
        draggable: false,
      },
      filterFn: filterByDateRange,
    }),
    columnHelper.accessor('company.registered_company_name', {
      cell: (data) => <CompanyCell application={data.row.original} />,
      header: t`Company name`,
      id: COLUMN_IDS.companyName,
      enableHiding: false,
      enableSorting: false,
      minSize: 272,
      meta: {
        draggable: false,
      },
    }),
    columnHelper.accessor('customer', {
      header: t`Applicant`,
      cell: (data) => <ApplicantCell customer={data.getValue()} />,
      id: COLUMN_IDS.applicant,
      enableSorting: false,
      enableHiding: false,
      minSize: 182,
    }),
    columnHelper.accessor('status', {
      header: t`Application status`,
      id: COLUMN_IDS.status,
      enableSorting: false,
      cell: (data) => <ApplicationStatusCell application={data.row.original} />,
      enableHiding: false,
      meta: {
        filterVariant: 'select',
      },
      filterFn: filterByStatus,
      minSize: 232,
    }),
    columnHelper.accessor((row) => getDurations(row), {
      header: t`Term length`,
      cell: (data) => <TermLengthCell termLengths={data.getValue()} />,
      id: COLUMN_IDS.termLength,
      enableSorting: false,
      meta: {
        filterVariant: 'select',
      },
      filterFn: filterByTermLength,
      minSize: 142,
    }),
    columnHelper.accessor(
      (row) => row.formal_offer_sets?.[0]?.formal_offers?.[0]?.max_amount,
      {
        header: t`Approval amount`,
        cell: (data) => <ApprovalAmountCell application={data.row.original} />,
        id: COLUMN_IDS.approvalAmount,
        meta: {
          filterVariant: 'numberRange',
        },
        filterFn: filterByNumberRange,
        minSize: 132,
      },
    ),
    columnHelper.accessor((row) => row.requested_products[0]?.amount, {
      header: t`Requested amount`,
      id: COLUMN_IDS.requestedAmount,
      cell: (data) => <RequestedAmountCell amount={data.getValue()} />,
      meta: {
        filterVariant: 'numberRange',
      },
      filterFn: filterByNumberRange,
      minSize: 132,
    }),
    columnHelper.accessor(
      (row) => row.formal_offer_sets?.[0]?.formal_offers?.[0]?.timeout,
      {
        header: t`Expiry date`,
        id: COLUMN_IDS.expiryDate,
        cell: (data) => (
          <ExpiryDateCell
            date={data.getValue()}
            application={data.row.original}
          />
        ),
        meta: {
          filterVariant: 'dateRange',
        },
        filterFn: filterByDateRange,
        minSize: 122,
      },
    ),
  ];

  if (showReferredByColumn) {
    columns.push(
      columnHelper.accessor((row) => row.introducer, {
        header: t`Referred by`,
        cell: (data) =>
          `${data.getValue()?.first_name ?? ''} ${data.getValue()?.last_name ?? ''}`,
        id: COLUMN_IDS.referredBy,
        enableSorting: false,
        meta: {
          filterVariant: 'text',
        },
        filterFn: (row, columnId, filterValue) =>
          filterByName(row, columnId, filterValue),
        minSize: 105,
      }),
    );
  }
  return columns;
};
